'use client';

import { cn } from '@/lib/utils';
import * as React from 'react';

type AddColumnBorder = {
  addColumnBorder?: boolean;
};

const Table = ({ addTopBottomBorder, className, ...props }: React.ComponentProps<'table'> & { addTopBottomBorder?: boolean; }): React.JSX.Element => {
  return (
    <div className="relative w-full overflow-x-auto"
      data-slot="table-container"
    >
      <table className={cn('w-full caption-bottom text-sm', addTopBottomBorder && 'border-t border-b', className)}
        data-slot="table"
        {...props}
      />
    </div>
  );
};

const TableHeader = ({ className, ...props }: React.ComponentProps<'thead'>): React.JSX.Element => {
  return (
    <thead className={cn('[&_tr]:border-b', className)}
      data-slot="table-header"
      {...props}
    />
  );
};

const TableBody = ({ className, ...props }: React.ComponentProps<'tbody'>): React.JSX.Element => {
  return (
    <tbody className={cn('[&_tr:last-child]:border-0', className)}
      data-slot="table-body"
      {...props}
    />
  );
};

const TableFooter = ({ className, ...props }: React.ComponentProps<'tfoot'>): React.JSX.Element => {
  return (
    <tfoot className={cn(
      'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
      className
    )}
    data-slot="table-footer"
    {...props}
    />
  );
};

const TableRow = ({ addColumnBorder, className, ...props }: React.ComponentProps<'tr'> & AddColumnBorder): React.JSX.Element => {
  return (
    <tr className={cn(
      'hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors',
      addColumnBorder && '[&>*]:border-r',
      className
    )}
    data-slot="table-row"
    {...props}
    />
  );
};

const TableHead = ({ addColumnBorder, className, ...props }: React.ComponentProps<'th'> & AddColumnBorder): React.JSX.Element => {
  return (
    <th className={cn(
      'text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      addColumnBorder && 'border-r',
      className
    )}
    data-slot="table-head"
    {...props}
    />
  );
};

const TableCell = ({ addColumnBorder, className, ...props }: React.ComponentProps<'td'> & AddColumnBorder): React.JSX.Element => {
  return (
    <td className={cn(
      'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      addColumnBorder && 'border-r',
      className
    )}
    data-slot="table-cell"
    {...props}
    />
  );
};

const TableCaption = ({
  className,
  ...props
}: React.ComponentProps<'caption'>): React.JSX.Element => {
  return (
    <caption className={cn('text-muted-foreground mt-4 text-sm', className)}
      data-slot="table-caption"
      {...props}
    />
  );
};

export {
  Table, TableBody, TableCaption, TableCell, TableFooter,
  TableHead, TableHeader, TableRow,
};
